import { apiClient, endpoints } from '../config';

/**
 * Service for manuscript-related API calls
 */
export const manuscriptService = {
  /**
   * Get all manuscripts (admin only)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of manuscripts
   */
  getAllManuscripts: async (params = {}) => {
    const response = await apiClient.get(endpoints.manuscripts.list, { params });
    return response.data;
  },

  /**
   * Get manuscript by ID
   * @param {string} id - Manuscript ID
   * @returns {Promise<Object>} - Manuscript details
   */
  getManuscriptById: async (id) => {
    const response = await apiClient.get(endpoints.manuscripts.details(id));
    return response.data;
  },

  /**
   * Get author's manuscripts
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of author's manuscripts
   */
  getAuthorManuscripts: async (params = {}) => {
    const response = await apiClient.get(endpoints.manuscripts.authorManuscripts, { params });
    return response.data;
  },

  /**
   * Create a new manuscript
   * @param {Object} manuscriptData - Manuscript data
   * @returns {Promise<Object>} - Created manuscript
   */
  createManuscript: async (manuscriptData) => {
    // Create FormData object for file uploads
    const formData = new FormData();

    // Add basic manuscript data
    formData.append('title', manuscriptData.title);
    formData.append('abstract', manuscriptData.abstract);
    formData.append('type', manuscriptData.type);
    formData.append('journalId', manuscriptData.journalId);

    // Add optional fields if they exist
    if (manuscriptData.content) {
      formData.append('content', manuscriptData.content);
    }
    if (manuscriptData.keywords) {
      formData.append('keywords', manuscriptData.keywords);
    }
    if (manuscriptData.references) {
      formData.append('references', manuscriptData.references);
    }
    if (manuscriptData.introduction) {
      formData.append('introduction', manuscriptData.introduction);
    }
    if (manuscriptData.theoreticalUnderpinnings) {
      formData.append('theoreticalUnderpinnings', manuscriptData.theoreticalUnderpinnings);
    }
    if (manuscriptData.methodology) {
      formData.append('methodology', manuscriptData.methodology);
    }
    if (manuscriptData.results) {
      formData.append('results', manuscriptData.results);
    }
    if (manuscriptData.discussion) {
      formData.append('discussion', manuscriptData.discussion);
    }
    if (manuscriptData.issueId) {
      formData.append('issueId', manuscriptData.issueId);
    }

    // Add authors data
    if (manuscriptData.authors && manuscriptData.authors.length > 0) {
      formData.append('authors', JSON.stringify(manuscriptData.authors));
    }

    // Add attachments if they exist
    if (manuscriptData.attachments && manuscriptData.attachments.length > 0) {
      // Group files by fileCategory
      const manuscriptFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'MANUSCRIPT');
      const figureFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'FIGURE');
      const tableFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'TABLE');
      const supplementaryFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'SUPPLEMENTARY');

      // Append files with the correct field names expected by the server
      manuscriptFiles.forEach(file => {
        formData.append('manuscript', file.originFileObj);
      });

      figureFiles.forEach(file => {
        formData.append('figures', file.originFileObj);
      });

      tableFiles.forEach(file => {
        formData.append('tables', file.originFileObj);
      });

      supplementaryFiles.forEach(file => {
        formData.append('supplementary', file.originFileObj);
      });
    }

    const response = await apiClient.post(endpoints.manuscripts.create, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  /**
   * Save manuscript as draft
   * @param {Object} manuscriptData - Manuscript data
   * @returns {Promise<Object>} - Saved draft manuscript
   */
  saveDraft: async (manuscriptData) => {
    console.log('Saving manuscript as draft with data:', manuscriptData);

    // Create FormData object for file uploads
    const formData = new FormData();

    // Add basic manuscript data
    formData.append('title', manuscriptData.title);
    formData.append('abstract', manuscriptData.abstract);
    formData.append('type', manuscriptData.type);
    formData.append('journalId', manuscriptData.journalId);

    // Add optional fields if they exist
    if (manuscriptData.content) {
      formData.append('content', manuscriptData.content);
    }
    if (manuscriptData.keywords) {
      formData.append('keywords', manuscriptData.keywords);
    }
    if (manuscriptData.references) {
      formData.append('references', manuscriptData.references);
    }
    if (manuscriptData.introduction) {
      formData.append('introduction', manuscriptData.introduction);
    }
    if (manuscriptData.theoreticalUnderpinnings) {
      formData.append('theoreticalUnderpinnings', manuscriptData.theoreticalUnderpinnings);
    }
    if (manuscriptData.methodology) {
      formData.append('methodology', manuscriptData.methodology);
    }
    if (manuscriptData.results) {
      formData.append('results', manuscriptData.results);
    }
    if (manuscriptData.discussion) {
      formData.append('discussion', manuscriptData.discussion);
    }
    if (manuscriptData.issueId) {
      formData.append('issueId', manuscriptData.issueId);
    }

    // Add authors data
    if (manuscriptData.authors && manuscriptData.authors.length > 0) {
      console.log('Adding authors data:', manuscriptData.authors);
      formData.append('authors', JSON.stringify(manuscriptData.authors));
    }

    // Add attachments if they exist
    if (manuscriptData.attachments && manuscriptData.attachments.length > 0) {
      console.log('Processing attachments:', manuscriptData.attachments);

      // Group files by fileCategory
      const manuscriptFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'MANUSCRIPT');
      const figureFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'FIGURE');
      const tableFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'TABLE');
      const supplementaryFiles = manuscriptData.attachments.filter(file => file.fileCategory === 'SUPPLEMENTARY');

      console.log('Manuscript files:', manuscriptFiles.length);
      console.log('Figure files:', figureFiles.length);
      console.log('Table files:', tableFiles.length);
      console.log('Supplementary files:', supplementaryFiles.length);

      // Append files with the correct field names expected by the server
      manuscriptFiles.forEach(file => {
        console.log('Adding manuscript file:', file.name);
        formData.append('manuscript', file.originFileObj);
      });

      figureFiles.forEach(file => {
        console.log('Adding figure file:', file.name);
        formData.append('figures', file.originFileObj);
      });

      tableFiles.forEach(file => {
        console.log('Adding table file:', file.name);
        formData.append('tables', file.originFileObj);
      });

      supplementaryFiles.forEach(file => {
        console.log('Adding supplementary file:', file.name);
        formData.append('supplementary', file.originFileObj);
      });
    } else {
      console.log('No attachments found in manuscript data');
    }

    console.log('Using endpoint:', endpoints.manuscripts.draft);
    try {
      const response = await apiClient.post(endpoints.manuscripts.draft, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Save draft response:', response);
      return response.data;
    } catch (error) {
      console.error('Error saving draft:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Update an existing manuscript
   * @param {string} id - Manuscript ID
   * @param {Object} manuscriptData - Updated manuscript data
   * @returns {Promise<Object>} - Updated manuscript
   */
  updateManuscript: async (id, manuscriptData) => {
    console.log('Updating manuscript with ID:', id);
    console.log('Update data:', manuscriptData);

    // Check if there are any new file attachments
    const hasNewFiles = manuscriptData.attachments && manuscriptData.attachments.some(
      file => file.originFileObj && !file.url
    );

    // If there are no new files, use simple JSON request
    if (!hasNewFiles) {
      console.log('No new files to upload, using simple PUT request');
      const response = await apiClient.put(endpoints.manuscripts.update(id), manuscriptData);
      return response.data;
    }

    // If there are new files, use FormData
    console.log('New files detected, using FormData for update');
    const formData = new FormData();

    // Add basic manuscript data
    formData.append('title', manuscriptData.title);
    formData.append('abstract', manuscriptData.abstract);
    formData.append('type', manuscriptData.type);
    formData.append('journalId', manuscriptData.journalId);

    // Keywords (new)
    if (manuscriptData.keywords !== undefined) {
      formData.append('keywords', manuscriptData.keywords ?? '');
    }
    // References
    if (manuscriptData.references !== undefined) {
      formData.append('references', manuscriptData.references ?? '');
    }
    // Legacy content sections — when key is present, send through (empty string blanks the column server-side)
    const legacyFields = ['content', 'introduction', 'theoreticalUnderpinnings', 'methodology', 'results', 'discussion'];
    for (const field of legacyFields) {
      if (Object.prototype.hasOwnProperty.call(manuscriptData, field)) {
        formData.append(field, manuscriptData[field] ?? '');
      }
    }
    if (manuscriptData.issueId) {
      formData.append('issueId', manuscriptData.issueId);
    }

    // Add authors data
    if (manuscriptData.authors && manuscriptData.authors.length > 0) {
      console.log('Adding authors data:', manuscriptData.authors);
      formData.append('authors', JSON.stringify(manuscriptData.authors));
    }

    // Add existing attachment IDs to keep
    const existingAttachments = manuscriptData.attachments
      .filter(file => file.uid && file.url)
      .map(file => file.uid);

    if (existingAttachments.length > 0) {
      console.log('Keeping existing attachments:', existingAttachments);
      formData.append('existingAttachments', JSON.stringify(existingAttachments));
    }

    // Add new attachments if they exist
    if (manuscriptData.attachments && manuscriptData.attachments.length > 0) {
      console.log('Processing attachments:', manuscriptData.attachments);

      // Group files by fileCategory
      const manuscriptFiles = manuscriptData.attachments.filter(
        file => file.fileCategory === 'MANUSCRIPT' && file.originFileObj && !file.url
      );
      const figureFiles = manuscriptData.attachments.filter(
        file => file.fileCategory === 'FIGURE' && file.originFileObj && !file.url
      );
      const tableFiles = manuscriptData.attachments.filter(
        file => file.fileCategory === 'TABLE' && file.originFileObj && !file.url
      );
      const supplementaryFiles = manuscriptData.attachments.filter(
        file => file.fileCategory === 'SUPPLEMENTARY' && file.originFileObj && !file.url
      );

      console.log('New manuscript files:', manuscriptFiles.length);
      console.log('New figure files:', figureFiles.length);
      console.log('New table files:', tableFiles.length);
      console.log('New supplementary files:', supplementaryFiles.length);

      // Append files with the correct field names expected by the server
      manuscriptFiles.forEach(file => {
        console.log('Adding manuscript file:', file.name);
        formData.append('manuscript', file.originFileObj);
      });

      figureFiles.forEach(file => {
        console.log('Adding figure file:', file.name);
        formData.append('figures', file.originFileObj);
      });

      tableFiles.forEach(file => {
        console.log('Adding table file:', file.name);
        formData.append('tables', file.originFileObj);
      });

      supplementaryFiles.forEach(file => {
        console.log('Adding supplementary file:', file.name);
        formData.append('supplementary', file.originFileObj);
      });
    }

    try {
      const response = await apiClient.put(endpoints.manuscripts.update(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Update manuscript response:', response);
      return response.data;
    } catch (error) {
      console.error('Error updating manuscript:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Delete a manuscript
   * @param {string} id - Manuscript ID
   * @returns {Promise<void>}
   */
  deleteManuscript: async (id) => {
    await apiClient.delete(endpoints.manuscripts.delete(id));
  },

  /**
   * Submit a manuscript for review
   * @param {string} id - Manuscript ID
   * @returns {Promise<Object>} - Submitted manuscript
   */
  submitManuscript: async (id) => {
    console.log('Submitting manuscript with ID:', id);
    console.log('Using endpoint:', endpoints.manuscripts.submit(id));
    try {
      const response = await apiClient.post(endpoints.manuscripts.submit(id));
      console.log('Submit manuscript response:', response);
      return response.data;
    } catch (error) {
      console.error('Error submitting manuscript:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Submit a manuscript revision
   * @param {string} id - Manuscript ID
   * @returns {Promise<Object>} - Revised manuscript
   */
  submitRevision: async (id) => {
    console.log('Submitting revision for manuscript with ID:', id);
    console.log('Using endpoint:', endpoints.manuscripts.submitRevision(id));
    try {
      const response = await apiClient.post(endpoints.manuscripts.submitRevision(id));
      console.log('Submit revision response:', response);
      return response.data;
    } catch (error) {
      console.error('Error submitting revision:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Get manuscripts by journal
   * @param {string} journalId - Journal ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of manuscripts for the journal
   */
  getManuscriptsByJournal: async (journalId, params = {}) => {
    const response = await apiClient.get(`/manuscripts/journal/${journalId}`, { params });
    return response.data;
  },

  /**
   * Get manuscripts by issue
   * @param {string} issueId - Issue ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of manuscripts for the issue
   */
  getManuscriptsByIssue: async (issueId, params = {}) => {
    const response = await apiClient.get(`/manuscripts/issue/${issueId}`, { params });
    return response.data;
  },

  /**
   * Get published manuscripts
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of published manuscripts
   */
  getPublishedManuscripts: async (params = {}) => {
    const response = await apiClient.get('/manuscripts/published', { params });
    return response.data;
  },

  /**
   * Get a published manuscript by ID
   * @param {string} id - Manuscript ID
   * @returns {Promise<Object>} - Published manuscript details
   */
  getPublishedManuscriptById: async (id) => {
    const response = await apiClient.get(`/manuscripts/published/${id}`);
    return response.data;
  },

  /**
   * Update manuscript status (editor only)
   * @param {string} id - Manuscript ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - Updated manuscript
   */
  updateManuscriptStatus: async (id, status) => {
    const response = await apiClient.put(`/manuscripts/${id}/status`, { status });
    return response.data;
  },

  /**
   * Delete a manuscript as chief editor
   * @param {string} id - Manuscript ID
   * @returns {Promise<void>}
   */
  deleteManuscriptByChiefEditor: async (id) => {
    await apiClient.delete(endpoints.manuscripts.chiefEditorDelete(id));
  },

  /**
   * Get manuscripts for journals created by the authenticated CHIEF_EDITOR
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} - List of manuscripts for journals created by the CHIEF_EDITOR
   */
  getChiefEditorManuscripts: async (params = {}) => {
    const response = await apiClient.get(endpoints.manuscripts.chiefEditorManuscripts, { params });
    return response.data;
  },

  /**
   * Get the download URL for a manuscript attachment
   * @param {string} attachmentId - Attachment ID
   * @returns {string} - URL for downloading the attachment
   */
  getAttachmentDownloadUrl: (attachmentId) => {
    return endpoints.attachments.downloadAttachment(attachmentId);
  }
};

export default manuscriptService;
